import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api } from "@/services/api";

interface FavoritesContextValue {
  /** Set of favorited booking source_ids for the current user. */
  favorites: ReadonlySet<string>;
  /** Whether the initial favorites load is in flight. */
  loading: boolean;
  /** Whether a booking is currently favorited. */
  isFavorite: (sourceId: string) => boolean;
  /**
   * Toggle (or explicitly set) a booking's favorite state. Optimistic: updates
   * the local set immediately and reverts on API failure. Returns the resulting
   * state.
   */
  toggleFavorite: (sourceId: string, next?: boolean) => Promise<boolean>;
  /** Re-fetch the favorites set from the server. */
  refresh: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const ids = await api.getFavoriteBookings();
      if (mountedRef.current) setFavorites(new Set(ids));
    } catch {
      // Non-fatal: favorites simply stay empty / stale.
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isFavorite = useCallback(
    (sourceId: string) => favorites.has(sourceId),
    [favorites],
  );

  const toggleFavorite = useCallback(
    async (sourceId: string, next?: boolean) => {
      const target = next ?? !favorites.has(sourceId);
      // Optimistic update.
      setFavorites((prev) => {
        const copy = new Set(prev);
        if (target) copy.add(sourceId);
        else copy.delete(sourceId);
        return copy;
      });
      try {
        const result = await api.setBookingFavorite(sourceId, target);
        // Reconcile with the server's authoritative state.
        if (mountedRef.current) {
          setFavorites((prev) => {
            const copy = new Set(prev);
            if (result) copy.add(sourceId);
            else copy.delete(sourceId);
            return copy;
          });
        }
        return result;
      } catch (err) {
        // Revert on failure.
        if (mountedRef.current) {
          setFavorites((prev) => {
            const copy = new Set(prev);
            if (target) copy.delete(sourceId);
            else copy.add(sourceId);
            return copy;
          });
        }
        throw err;
      }
    },
    [favorites],
  );

  const value = useMemo<FavoritesContextValue>(
    () => ({ favorites, loading, isFavorite, toggleFavorite, refresh }),
    [favorites, loading, isFavorite, toggleFavorite, refresh],
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return ctx;
}

export function useFavoritesOptional(): FavoritesContextValue | null {
  return useContext(FavoritesContext);
}
